import React from 'react';
import GroupPreview from './GroupPreview';
import { PreviewGroup } from '../../hooks/useGroupCalculation';

interface OrderSummaryDisplayProps {
  previewGroups: PreviewGroup[];
  totalPageCounts: { bw: number; color: number };
  uploadError: string | null;
  isCalculating?: boolean;
}

const OrderSummaryDisplay: React.FC<OrderSummaryDisplayProps> = ({
  previewGroups,
  totalPageCounts,
  uploadError,
  isCalculating = false
}) => {
  const totalPrice = previewGroups.reduce((sum, g) => sum + g.price, 0);

  return (
    <div className="mb-6">
      <p className="text-sm sm:text-base text-gray-500 mb-4">Review your order before proceeding to payment</p>

      <div className="border-t border-b border-gray-200 py-3 sm:py-4 mb-4">
        <h3 className="font-medium text-base mb-3">Print Groups ({previewGroups.length})</h3>
        {isCalculating ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="ml-3 text-sm text-gray-500">Calculating pricing...</p>
          </div>
        ) : previewGroups.length > 0 ? (
          <div className="space-y-3">
            {previewGroups.map((group, index) => (
              <GroupPreview key={group.id} group={group} index={index} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">No groups to display</p>
        )}
      </div>

      <div className="mb-4 bg-gray-50 rounded-lg p-3">
        <h3 className="font-medium text-base mb-2">Page Summary</h3>
        <div className="text-sm text-gray-600 space-y-1">
          <div className="flex justify-between">
            <span>Black & White:</span>
            <span className="font-medium">{totalPageCounts.bw} pages</span>
          </div>
          <div className="flex justify-between">
            <span>Color:</span>
            <span className="font-medium">{totalPageCounts.color} pages</span>
          </div>
          <div className="flex justify-between pt-1 border-t border-gray-200">
            <span className="font-medium">Total Pages:</span>
            <span className="font-bold">{totalPageCounts.bw + totalPageCounts.color}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center font-bold text-lg sm:text-xl bg-blue-50 p-3 sm:p-4 rounded-lg">
        <span>Total</span>
        <span className="text-blue-600">₹{totalPrice.toFixed(2)}</span>
      </div>

      {uploadError && (
        <div className="mt-3 text-red-600 text-sm text-center break-words">
          {uploadError}
        </div>
      )}
    </div>
  );
};

export default OrderSummaryDisplay;
